import { BitMask, makeMask, maskGet, maskSet } from './textGen';

/**
 * A joined script alphabet, and a slant for rectilinear ones.
 *
 * Handwriting is the one style a traced font cannot give at cell resolution.
 * A script typeface rasterised into a 7-row mask loses exactly what makes it
 * handwriting — the thin connecting strokes fall below one cell and disappear,
 * leaving disconnected blobs. So the joined hand is defined here the same way
 * the box alphabet is: one stroke per pen movement, drawn directly on the grid.
 *
 * Every glyph enters at the left of the baseline row and leaves at the right of
 * it, so consecutive letters meet and the word reads as one continuous line —
 * which is what separates script from merely italic.
 */

export const SCRIPT_W = 6;
export const SCRIPT_H = 7;

/** The row letters sit on, where the joining strokes meet. */
const BASELINE = 5;

/**
 * '#' is a stroke cell, '.' is empty.
 *
 * Cursive is a lowercase hand — capitals in a joined script are a separate,
 * much more ornate alphabet — so one set of forms serves both cases rather
 * than pretending to a distinction the style does not have.
 */
const GLYPHS: Record<string, string[]> = {
  A: ['......', '......', '.####.', '#....#', '#....#', '######', '......'],
  B: ['#.....', '#.....', '#.....', '#####.', '#....#', '######', '......'],
  C: ['......', '......', '.#####', '#.....', '#.....', '.#####', '......'],
  D: ['.....#', '.....#', '.#####', '#....#', '#....#', '######', '......'],
  E: ['......', '......', '.####.', '#....#', '#####.', '.#####', '......'],
  F: ['..####', '.#....', '.#....', '####..', '.#....', '.#####', '......'],
  G: ['......', '......', '.####.', '#....#', '#....#', '.#####', '..###.'],
  H: ['#.....', '#.....', '#.###.', '##...#', '#....#', '#....#', '......'],
  I: ['......', '..#...', '......', '..#...', '..#...', '######', '......'],
  J: ['......', '...#..', '......', '...#..', '...#..', '...#..', '.###..'],
  K: ['#.....', '#.....', '#...#.', '#..#..', '##.#..', '#..###', '......'],
  L: ['.##...', '..#...', '..#...', '..#...', '..#...', '..####', '......'],
  M: ['......', '......', '#.##.#', '##..##', '#....#', '######', '......'],
  N: ['......', '......', '#.###.', '##...#', '#....#', '######', '......'],
  O: ['......', '......', '.####.', '#....#', '#....#', '.#####', '......'],
  P: ['......', '......', '#####.', '#....#', '#####.', '#####.', '#.....'],
  Q: ['......', '......', '.####.', '#....#', '#....#', '.#####', '....##'],
  R: ['......', '......', '#.###.', '##....', '#.....', '######', '......'],
  S: ['......', '......', '.#####', '#.....', '.####.', '######', '......'],
  T: ['..#...', '..#...', '#####.', '..#...', '..#...', '..####', '......'],
  U: ['......', '......', '#....#', '#....#', '#....#', '.#####', '......'],
  V: ['......', '......', '#....#', '#....#', '.#..#.', '##..##', '......'],
  W: ['......', '......', '#....#', '#....#', '#.##.#', '######', '......'],
  X: ['......', '......', '#....#', '.#..#.', '..##..', '##..##', '......'],
  Y: ['......', '......', '#....#', '#....#', '.#####', '....#.', '.###..'],
  Z: ['......', '......', '######', '...##.', '.##...', '######', '......'],

  '0': ['......', '.####.', '#....#', '#....#', '#....#', '.#####', '......'],
  '1': ['......', '..##..', '.#.#..', '...#..', '...#..', '..####', '......'],
  '2': ['......', '.####.', '#....#', '...##.', '.##...', '######', '......'],
  '3': ['......', '.####.', '....#.', '..###.', '.....#', '#####.', '......'],
  '4': ['......', '...##.', '..#.#.', '.#..#.', '######', '....##', '......'],
  '5': ['......', '######', '#.....', '#####.', '.....#', '#####.', '......'],
  '6': ['......', '..###.', '.#....', '#####.', '#....#', '.#####', '......'],
  '7': ['......', '######', '....#.', '...#..', '..#...', '.#####', '......'],
  '8': ['......', '.####.', '#....#', '.####.', '#....#', '.#####', '......'],
  '9': ['......', '.####.', '#....#', '.#####', '....#.', '.####.', '......'],

  ' ': ['......', '......', '......', '......', '......', '......', '......'],
  '.': ['......', '......', '......', '......', '......', '..#...', '......'],
  ',': ['......', '......', '......', '......', '......', '..#...', '.#....'],
  ':': ['......', '......', '..#...', '......', '..#...', '......', '......'],
  ';': ['......', '......', '..#...', '......', '..#...', '.#....', '......'],
  '!': ['..#...', '..#...', '..#...', '..#...', '......', '..#...', '......'],
  '?': ['.####.', '#....#', '...##.', '..#...', '......', '..#...', '......'],
  '-': ['......', '......', '......', '.####.', '......', '......', '......'],
  '_': ['......', '......', '......', '......', '......', '......', '######'],
  "'": ['..#...', '.#....', '......', '......', '......', '......', '......'],
  '"': ['.#.#..', '#.#...', '......', '......', '......', '......', '......'],
  '&': ['......', '.###..', '#...#.', '.##...', '#..#.#', '.##.##', '......'],
  '(': ['...#..', '..#...', '.#....', '.#....', '..#...', '...#..', '......'],
  ')': ['.#....', '..#...', '...#..', '...#..', '..#...', '.#....', '......'],
  '/': ['.....#', '....#.', '...#..', '..#...', '.#....', '#.....', '......'],
  '@': ['.####.', '#....#', '#.###.', '#.#.#.', '#.###.', '.####.', '......'],
};

export function hasScriptGlyph(char: string): boolean {
  return GLYPHS[char.toUpperCase()] !== undefined;
}

export interface ScriptTextOptions {
  /**
   * Cells between letters. Zero is the point of the style — the exit stroke of
   * one glyph lands on the entry stroke of the next and the hand joins up.
   */
  letterSpacing: number;
  lineSpacing: number;
  /** Leans the letters to the right, the way a fast hand does. */
  slant: boolean;
}

export const DEFAULT_SCRIPT_OPTIONS: ScriptTextOptions = {
  letterSpacing: 0,
  lineSpacing: 1,
  slant: false,
};

/**
 * Lays out joined script into a bitmap, one dot per cell.
 *
 * Where two glyphs meet, the baseline cells of both land on the same column, so
 * the join is a single continuous stroke rather than a visible seam.
 */
export function renderScriptText(text: string, options: Partial<ScriptTextOptions> = {}): BitMask {
  const opts = { ...DEFAULT_SCRIPT_OPTIONS, ...options };
  const lines = text.split('\n');
  const spacing = Math.max(0, opts.letterSpacing);

  const stepX = SCRIPT_W + spacing;
  const stepY = SCRIPT_H + Math.max(0, opts.lineSpacing);
  const longest = Math.max(1, ...lines.map((l) => [...l].length));
  const width = longest * SCRIPT_W + (longest - 1) * spacing;
  const height = lines.length * SCRIPT_H + (lines.length - 1) * Math.max(0, opts.lineSpacing);
  const mask = makeMask(Math.max(1, width), Math.max(1, height));

  lines.forEach((line, row) => {
    const chars = [...line];
    chars.forEach((raw, col) => {
      const glyph = GLYPHS[raw.toUpperCase()];
      if (!glyph) return;
      const originX = col * stepX;
      const originY = row * stepY;
      for (let y = 0; y < SCRIPT_H; y++) {
        for (let x = 0; x < SCRIPT_W; x++) {
          if (glyph[y][x] === '#') maskSet(mask, originX + x, originY + y, true);
        }
      }

      /*
       * Bridge the gap to the next letter when the spacing opens one. Without
       * this the hand breaks into separate letters at any spacing above zero,
       * which is the one thing a joined script must not do.
       */
      const next = chars[col + 1];
      if (spacing > 0 && next && next !== ' ' && raw !== ' ' && GLYPHS[next.toUpperCase()]) {
        for (let i = 0; i < spacing; i++) {
          maskSet(mask, originX + SCRIPT_W + i, originY + BASELINE, true);
        }
      }
    });
  });

  return opts.slant ? slantMask(mask, SCRIPT_H) : mask;
}

/**
 * Leans a mask to the right.
 *
 * Each row is pushed sideways by an amount that grows towards the top, so a
 * vertical stem becomes a shallow staircase. Box connectivity turns that
 * staircase into joined corner pieces, which is why the shift is at most two
 * cells over the height of a glyph: a steeper lean produces steps the box
 * characters cannot close, and the letter falls apart into fragments.
 *
 * `band` is the height of one line of text, so every line leans the same way
 * instead of the slant accumulating down a paragraph.
 */
export function slantMask(mask: BitMask, band: number, steps = 2): BitMask {
  const shiftFor = (y: number): number => {
    const withinLine = ((y % band) + band) % band;
    return Math.round(((band - 1 - withinLine) / Math.max(1, band - 1)) * steps);
  };

  const extra = steps;
  const out = makeMask(mask.width + extra, mask.height);
  for (let y = 0; y < mask.height; y++) {
    const dx = shiftFor(y);
    for (let x = 0; x < mask.width; x++) {
      if (maskGet(mask, x, y)) maskSet(out, x + dx, y, true);
    }
  }
  return out;
}

/** Characters the alphabet covers, for the panel's hint. */
export function scriptCoverage(): string {
  return Object.keys(GLYPHS)
    .filter((k) => k !== ' ')
    .join('');
}
